import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type PipelineStatusKey } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { type DashboardRange, useDashboardDateRange } from '@/features/dashboard/hooks';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryPipelines } from '@/features/pipelines/data';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { attachRowDailyHistoryRunLinks } from '@/features/shared/components/dailyHistoryLinks';
import {
  queryKeys,
  useAccessToken,
  usePersistedStringArray,
  useResolvedFeatureFlags,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildPipelinesToolbarState } from './pipelineChartSectionModel';
import { usePipelinesOverviewModel } from './usePipelinesOverviewModel';
import type { ExecutionStatusKey } from '@/features/metrics/metricsHelpers';
import type { PipelinesTablePanelProps } from '@/features/pipelines/components/PipelinesTablePanel';
import type { StackedMetricsStatusChartSectionProps } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import type { EntityDailyHistoryRow } from '@/features/shared/components/EntityDailyHistoryPanel';
import type { DailyHistorySortKey } from '@/features/shared/components/EntityDailyHistoryPanel.types';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { BuildStatus } from '@flowqa/shared';

type UsePipelinesPageViewModelResult = {
  showPageSkeleton: boolean;
  metricSectionProps: StackedMetricsStatusChartSectionProps<PipelineStatusKey>;
  tablePanelProps: PipelinesTablePanelProps;
  dailyHistoryRows: EntityDailyHistoryRow[];
  dailyHistoryLoading: boolean;
  dailyHistoryNoRowsText: string;
  dailyHistoryDateRangeLabel: string | null;
  dailyHistoryPage: number;
  dailyHistoryPageSize: number;
  dailyHistorySortDirection: 'asc' | 'desc';
  dailyHistorySortKey: DailyHistorySortKey;
  dailyHistoryTotalCount: number;
  setDailyHistoryPage: (nextPage: number) => void;
  setDailyHistoryPageSize: (nextPageSize: number) => void;
  setDailyHistorySortDirection: (nextSortDirection: 'asc' | 'desc') => void;
  setDailyHistorySortKey: (nextSortKey: DailyHistorySortKey) => void;
};
export const usePipelinesPageViewModel = (): UsePipelinesPageViewModelResult => {
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState<DashboardRange>('7d');
  const [activeTab, setActiveTab] = useState<'pipelines' | 'builds'>('pipelines');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<PipelineStatusKey[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:pipelines');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [dailyHistoryPage, setDailyHistoryPage] = useState(0);
  const [dailyHistoryPageSize, setDailyHistoryPageSize] = useState(5);
  const [dailyHistorySortKey, setDailyHistorySortKey] = useState<DailyHistorySortKey>('name');
  const [dailyHistorySortDirection, setDailyHistorySortDirection] = useState<'asc' | 'desc'>('asc');
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const deltasEnabled = featureFlags?.deltas ?? true;
  const showDelta = deltasEnabled && !isLatestMode && !isAllMode && previousRangeStartIso !== null;
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const selectedChartStatuses = useMemo<ExecutionStatusKey[]>(() => {
    const mapped = selectedStatuses
      .map((status): ExecutionStatusKey | null => {
        if (status === 'completed') return 'passed';
        if (status === 'failed') return 'failed';
        return null;
      })
      .filter((status): status is ExecutionStatusKey => status !== null);
    return Array.from(new Set(mapped));
  }, [selectedStatuses]);
  const hasUnsupportedChartStatusSelection =
    selectedStatuses.length > 0 && selectedChartStatuses.length === 0;
  const selectedBuildStatuses = useMemo<BuildStatus[]>(
    () => selectedStatuses.filter((status): status is BuildStatus => status !== 'none'),
    [selectedStatuses],
  );
  const hasOnlyNoBuildsSelected = selectedStatuses.length > 0 && selectedBuildStatuses.length === 0;
  const {
    statusFilterOptions,
    buildsNoRowsText,
    visiblePipelineMetricCards,
    modelOverride,
    currentSummaryLoading,
    hasCurrentSummary,
  } = usePipelinesOverviewModel({
    accessToken,
    selectedProjectId,
    selectedOrganisationId: selectedOrganisationId ?? null,
    range,
    rangeStartIso,
    rangeEndIso,
    selectedStartDay,
    selectedEndDay,
    previousRangeStartIso,
    previousRangeEndIso,
    debouncedSearchValue,
    selectedBuildStatuses,
    selectedTags,
    hasOnlyNoBuildsSelected,
    hasUnsupportedChartStatusSelection,
    thresholds: thresholds.pipelines,
    showDelta,
  });
  const pickerStartDay = selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null;
  const pickerEndDay = selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null;

  const handleStartDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const resetRangeDates = useCallback((next: CloneDateQuickFilter) => {
    setRange(next);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);

  const chartToolbarQuickFilter: CloneDateQuickFilter =
    range === '14d' || range === '30d' || range === 'latest' ? range : '7d';
  const forcedLoadState = searchParams.get('loadState');
  const forcedPageLoadState = searchParams.get('pageLoadState');
  const showForcedLoadingState = forcedLoadState === 'loading';
  const showPageSkeleton =
    forcedPageLoadState === 'loading' ||
    (Boolean(accessToken) && currentSummaryLoading && !hasCurrentSummary);
  const toolbarState = useMemo(
    () =>
      buildPipelinesToolbarState({
        search: debouncedSearchValue,
        setSearch: setDebouncedSearchValue,
        selectedStatuses,
        setSelectedStatuses,
        selectedTags,
        setSelectedTags,
        noRowsText: buildsNoRowsText,
        dateQuickFilter: chartToolbarQuickFilter,
        onDateQuickFilterChange: resetRangeDates,
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        onFromDateChange: handleStartDayChange,
        onToDateChange: handleEndDayChange,
      }),
    [
      buildsNoRowsText,
      chartToolbarQuickFilter,
      debouncedSearchValue,
      handleEndDayChange,
      handleStartDayChange,
      resetRangeDates,
      selectedEndDay,
      selectedStartDay,
      selectedStatuses,
      selectedTags,
      setSelectedTags,
    ],
  );
  const dailyHistoryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        projectId: selectedProjectId,
        organisationId: selectedOrganisationId,
        search: debouncedSearchValue,
        selectedBuildStatuses,
        dailyHistoryPage,
        dailyHistoryPageSize,
        dailyHistorySortDirection,
        dailyHistorySortKey,
        selectedTags,
        startDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
        endDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
      }),
    [
      debouncedSearchValue,
      dailyHistoryPage,
      dailyHistoryPageSize,
      dailyHistorySortDirection,
      dailyHistorySortKey,
      rangeEndIso,
      rangeStartIso,
      selectedBuildStatuses,
      selectedEndDay,
      selectedOrganisationId,
      selectedProjectId,
      selectedStartDay,
      selectedTags,
    ],
  );
  const dailyHistoryQuery = useQuery({
    queryKey: queryKeys.dataGrid('pipelines-daily-history', dailyHistoryFiltersKey),
    enabled: Boolean(accessToken),
    queryFn: async () =>
      await queryPipelines(selectedProjectId, selectedOrganisationId ?? undefined, {
        page: dailyHistoryPage + 1,
        pageSize: dailyHistoryPageSize,
        search: debouncedSearchValue,
        sorts: [{ field: dailyHistorySortKey, direction: dailyHistorySortDirection }],
        filters: mergeFilters(
          selectedStatuses.length > 0
            ? buildInFilterWithNull(
                'latestBuildStatus',
                selectedStatuses.includes('none')
                  ? [null, ...selectedBuildStatuses]
                  : selectedBuildStatuses,
              )
            : [],
          buildInFilter('tags', selectedTags),
        ),
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
        include: {
          latestBuild: true,
          dailyHistory: true,
        },
      }),
  });
  useEffect(() => {
    setDailyHistoryPage(0);
  }, [
    debouncedSearchValue,
    rangeEndIso,
    rangeStartIso,
    selectedBuildStatuses,
    selectedEndDay,
    selectedOrganisationId,
    selectedProjectId,
    selectedStartDay,
    selectedTags,
  ]);
  const dailyHistoryRows = useMemo(
    () =>
      (dailyHistoryQuery.data?.items ?? []).map((pipeline) =>
        attachRowDailyHistoryRunLinks(
          {
            id: pipeline.id,
            name: pipeline.name,
            href: `/pipelines/${resolvePipelineSlug(pipeline)}`,
            dailyHistory: pipeline.dailyHistory ?? [],
          },
          'pipeline',
        ),
      ),
    [dailyHistoryQuery.data?.items],
  );
  const dailyHistoryDateRangeLabel = useMemo(() => {
    if (!pickerStartDay || !pickerEndDay) return null;
    return `${dayjs(pickerStartDay).format('MMM D, YYYY')} - ${dayjs(pickerEndDay).format('MMM D, YYYY')}`;
  }, [pickerEndDay, pickerStartDay]);
  return {
    showPageSkeleton,
    metricSectionProps: {
      title: 'Status',
      searchAriaLabel: 'Search pipelines',
      noRowsBaseText: 'No builds found',
      scope: 'pipeline',
      projectId: selectedProjectId,
      availableTags: tagOptions.map((option) => option.value),
      statusFilterLabel: 'Status',
      metricCards: visiblePipelineMetricCards,
      statusOptions: statusFilterOptions,
      toolbarState,
      modelOverride,
      loading: showForcedLoadingState,
      suppressAnimation: false,
    },
    tablePanelProps: {
      activeTab,
      onActiveTabChange: setActiveTab,
      selectedProjectId: selectedProjectId ?? null,
      selectedOrganisationId: selectedOrganisationId ?? null,
      deltasEnabled,
      pipelineSearchValue: debouncedSearchValue,
      onPipelineSearchChange: setDebouncedSearchValue,
      pipelineFilterStatuses: selectedStatuses,
      onPipelineFilterStatusesChange: setSelectedStatuses,
      pipelineFilterTags: selectedTags,
      onPipelineFilterTagsChange: setSelectedTags,
      pipelineDateQuickFilter: chartToolbarQuickFilter,
      onPipelineDateQuickFilterChange: resetRangeDates,
      pipelineFromDate: selectedStartDay ? dayjs(selectedStartDay) : null,
      pipelineToDate: selectedEndDay ? dayjs(selectedEndDay) : null,
      onPipelineFromDateChange: (next) => {
        handleStartDayChange(next ? next.format('YYYY-MM-DD') : null);
      },
      onPipelineToDateChange: (next) => {
        handleEndDayChange(next ? next.format('YYYY-MM-DD') : null);
      },
    },
    dailyHistoryRows,
    dailyHistoryLoading: showForcedLoadingState || dailyHistoryQuery.isFetching,
    dailyHistoryNoRowsText: buildsNoRowsText,
    dailyHistoryDateRangeLabel,
    dailyHistoryPage,
    dailyHistoryPageSize,
    dailyHistorySortDirection,
    dailyHistorySortKey,
    dailyHistoryTotalCount: dailyHistoryQuery.data?.total ?? 0,
    setDailyHistoryPage,
    setDailyHistoryPageSize,
    setDailyHistorySortDirection,
    setDailyHistorySortKey,
  };
};
